import React, { useState } from 'react';
import { Paper } from '@mui/material';
import PaymentIcon from '@mui/icons-material/Payment';
import {
  Box, IconButton, Tooltip, Stack, Dialog, DialogTitle,
  DialogContent, DialogActions, Button, Typography, TextField, Divider
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import VisibilityIcon from '@mui/icons-material/Visibility';
import PaidIcon from '@mui/icons-material/Paid';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import FilterListIcon from '@mui/icons-material/FilterList';
import { useNavigate } from 'react-router-dom';
import DynamicDataGrid from '../components/DynamicDataGrid';
import { useDispatch } from 'react-redux';
import { deletePayment } from '../slices/financeSlice';
import { useToast } from '../context/ToastContext';
import { motion } from 'framer-motion';

const MotionPaper = motion(Paper);

/**
 * Controlled table: filtering/pagination state lives in FinancialPage (which
 * owns the server-side fetch). This component only renders the summary cards,
 * the date filter card and the grid, and reports filter/pagination changes upward.
 */
const FinancialTable = ({
  data,
  rowCount,
  loading,
  paginationModel,
  onPaginationModelChange,
  filterValues,
  onFilterChange,
  summary,
  onDeleted,
  showSummaryCards = false,
  getLatestPaymentDate,
}) => {
  const navigate = useNavigate();
  const [openDialog, setOpenDialog] = useState(false);
  const dispatch = useDispatch();
  const [selectedPaymentId, setSelectedPaymentId] = useState(null);
  const { showToast } = useToast();

  const handleView = (id) => {
    navigate(`/finance/view/${id}`);
  };

  const handleDeleteClick = (id) => {
    setSelectedPaymentId(id);
    setOpenDialog(true);
  };

  const handleConfirmDelete = () => {
    dispatch(deletePayment(selectedPaymentId))
      .unwrap()
      .then(() => {
        if (onDeleted) onDeleted();
        showToast('تم حذف الدفعة بنجاح', 'success');
      })
      .catch(() => {
        showToast('فشل حذف الدفعة', 'error');
      });
  
    setOpenDialog(false);
  };

  const handleCancelDelete = () => {
    setOpenDialog(false);
  };

  // جلب الدور من localStorage
  const userRole = localStorage.getItem('role');

  const columns = [
    {
      field: 'number',
      headerName: 'رقم السند',
      flex: 1,
      renderHeader: () => (
        <Box sx={{ width: '100%' }}>
          <Typography sx={{ fontWeight: 700, color: '#1E1B4B', fontSize: '0.875rem', fontFamily: 'Cairo, Tajawal, sans-serif' }}>رقم السند</Typography>
          <TextField
            variant="standard"
            value={filterValues.number}
            onChange={(e) => onFilterChange('number', e.target.value)}
            placeholder="بحث"
            size="small"
            InputProps={{ disableUnderline: true, sx: { fontSize: '0.75rem', color: '#6B7280', fontFamily: 'Cairo, Tajawal, sans-serif' } }}
          />
        </Box>
      )
    },
    {
      field: 'type',
      headerName: 'نوع الجهة',
      flex: 1,
      renderHeader: () => (
        <Box sx={{ width: '100%' }}>
          <Typography sx={{ fontWeight: 700, color: '#1E1B4B', fontSize: '0.875rem', fontFamily: 'Cairo, Tajawal, sans-serif' }}>نوع الجهة</Typography>
          <TextField
            variant="standard"
            value={filterValues.type}
            onChange={(e) => onFilterChange('type', e.target.value)}
            placeholder="بحث"
            size="small"
            InputProps={{ disableUnderline: true, sx: { fontSize: '0.75rem', color: '#6B7280', fontFamily: 'Cairo, Tajawal, sans-serif' } }}
          />
        </Box>
      )
    },
    {
      field: 'target_name',
      headerName: 'اسم الطالب / الموظف / المورد',
      flex: 1.5,
      renderHeader: () => (
        <Box sx={{ width: '100%' }}>
          <Typography sx={{ fontWeight: 700, color: '#1E1B4B', fontSize: '0.875rem', fontFamily: 'Cairo, Tajawal, sans-serif' }}>الاسم</Typography>
          <TextField
            variant="standard"
            value={filterValues.target_name}
            onChange={(e) => onFilterChange('target_name', e.target.value)}
            placeholder="بحث"
            size="small"
            InputProps={{ disableUnderline: true, sx: { fontSize: '0.75rem', color: '#6B7280', fontFamily: 'Cairo, Tajawal, sans-serif' } }}
          />
        </Box>
      )
    },
    { field: 'amount', headerName: 'المبلغ (شيكل)', flex: 0.8 },
    { field: 'date', headerName: 'تاريخ الدفع', flex: 0.8 },
    {
      field: 'payment_type',
      headerName: 'طريقة الدفع',
      flex: 1,
      renderHeader: () => (
        <Box sx={{ width: '100%' }}>
          <Typography sx={{ fontWeight: 700, color: '#1E1B4B', fontSize: '0.875rem', fontFamily: 'Cairo, Tajawal, sans-serif' }}>طريقة الدفع</Typography>
          <TextField
            variant="standard"
            value={filterValues.method}
            onChange={(e) => onFilterChange('method', e.target.value)}
            placeholder="بحث"
            size="small"
            InputProps={{ disableUnderline: true, sx: { fontSize: '0.75rem', color: '#6B7280', fontFamily: 'Cairo, Tajawal, sans-serif' } }}
          />
        </Box>
      )
    },
    {
      field: 'actions',
      headerName: 'الإجراءات',
      flex: 1.5,
      sortable: false,
      filterable: false,
      disableColumnMenu: true,
      renderCell: (params) => (
        <Stack useFlexGap direction="row" spacing={1} justifyContent="center" width="100%">
          <Tooltip title="عرض التفاصيل">
            <IconButton
              size="small"
              onClick={() => handleView(params.row.id)}
              sx={{
                color: '#4338CA',
                borderRadius: 2,
                transition: 'all 0.2s',
                '&:hover': { bgcolor: '#EEF2FF', color: '#4338CA' },
              }}
            >
              <VisibilityIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          {userRole === 'manager' && (
            <Tooltip title="حذف الدفعة">
              <IconButton
                size="small"
                onClick={() => handleDeleteClick(params.row.id)}
                sx={{
                  color: '#DC2626',
                  borderRadius: 2,
                  transition: 'all 0.2s',
                  '&:hover': { bgcolor: '#FEE2E2', color: '#DC2626' },
                }}
              >
                <DeleteIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
        </Stack>
      )
    }
  ];

  // مجموع المبالغ وعدد السندات لكل النتائج المفلترة (محسوبة في الخادم)
  const totalAmount = summary?.total_amount || 0;
  const totalCount = summary?.count || 0;

  return (
    // 🎨 Design System Applied
    <Box sx={{ direction: 'rtl', fontFamily: 'Cairo, Tajawal, sans-serif', p: { xs: 0, sm: 0 }, overflowX: 'auto', maxWidth: '100vw' }}>
      {showSummaryCards && (
        <>
          <Stack useFlexGap direction={{ xs: 'column', sm: 'row' }} spacing={2} mb={3}>
            {[
              {
                label: 'عدد السندات',
                value: totalCount,
                icon: <PaymentIcon />,
                bg: '#EEF2FF',
                color: '#4338CA',
                delay: 0.1,
              },
              {
                label: 'مجموع المبالغ',
                value: `${totalAmount.toLocaleString('en-US')} ₪`,
                icon: <PaidIcon />,
                bg: '#DCFCE7',
                color: '#16A34A',
                delay: 0.18,
              },
              {
                label: 'آخر سند',
                value: getLatestPaymentDate ? getLatestPaymentDate() : '',
                icon: <AccessTimeIcon />,
                bg: '#E0F2FE',
                color: '#0284C7',
                delay: 0.26,
                small: true,
              },
            ].map((card) => (
              <MotionPaper
                key={card.label}
                component={motion.div}
                elevation={0}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.35, delay: card.delay, ease: 'easeOut' }}
                whileHover={{ y: -4 }}
                sx={{
                  flex: 1,
                  p: 2.5,
                  minWidth: 180,
                  borderRadius: 3,
                  bgcolor: '#fff',
                  boxShadow: '0 2px 12px rgba(67,56,202,0.07)',
                  border: '1px solid #E5E7EB',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 2,
                  transition: 'box-shadow 0.2s',
                  '&:hover': { boxShadow: '0 6px 20px rgba(67,56,202,0.12)' },
                }}
              >
                <Box
                  sx={{
                    bgcolor: card.bg,
                    color: card.color,
                    borderRadius: 2.5,
                    width: 48,
                    height: 48,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0,
                  }}
                >
                  {card.icon}
                </Box>
                <Box sx={{ minWidth: 0 }}>
                  <Typography
                    sx={{
                      color: '#6B7280',
                      fontSize: '0.875rem',
                      fontWeight: 500,
                      fontFamily: 'Cairo, Tajawal, sans-serif',
                    }}
                  >
                    {card.label}
                  </Typography>
                  <Typography
                    sx={{
                      fontSize: card.small ? '1rem' : '1.5rem',
                      fontWeight: 800,
                      color: '#1E1B4B',
                      fontFamily: 'Cairo, Tajawal, sans-serif',
                      lineHeight: 1.3,
                    }}
                  >
                    {card.value}
                  </Typography>
                </Box>
              </MotionPaper>
            ))}
          </Stack>
        </>
      )}
      {/* فلتر التاريخ */}
      <MotionPaper
        component={motion.div}
        elevation={0}
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.35, delay: 0.1, ease: 'easeOut' }}
        sx={{
          mb: 2,
          p: { xs: 2, sm: 2.5 },
          borderRadius: 3,
          bgcolor: '#fff',
          boxShadow: '0 2px 12px rgba(67,56,202,0.07)',
          border: '1px solid #E5E7EB',
        }}
      >
        <Stack useFlexGap direction="row" alignItems="center" spacing={1} mb={2}>
          <FilterListIcon sx={{ color: '#4338CA', fontSize: 20 }} />
          <Typography
            sx={{ fontWeight: 700, color: '#1E1B4B', fontFamily: 'Cairo, Tajawal, sans-serif' }}
          >
            تصفية حسب التاريخ
          </Typography>
        </Stack>
        <Divider sx={{ mb: 2, borderColor: '#E5E7EB' }} />
        <Stack useFlexGap direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems="center">
          <TextField
            type="date"
            label="من"
            variant="outlined"
            value={filterValues.dateFrom}
            onChange={(e) => onFilterChange('dateFrom', e.target.value)}
            InputLabelProps={{ shrink: true }}
            size="small"
            sx={{
              minWidth: 140,
              '& .MuiOutlinedInput-root': {
                borderRadius: 2,
                fontFamily: 'Cairo, Tajawal, sans-serif',
              },
            }}
          />
          <TextField
            type="date"
            label="إلى"
            variant="outlined"
            value={filterValues.dateTo}
            onChange={(e) => onFilterChange('dateTo', e.target.value)}
            InputLabelProps={{ shrink: true }}
            size="small"
            sx={{
              minWidth: 140,
              '& .MuiOutlinedInput-root': {
                borderRadius: 2,
                fontFamily: 'Cairo, Tajawal, sans-serif',
              },
            }}
          />
        </Stack>
      </MotionPaper>
      <DynamicDataGrid
        columns={columns}
        rows={data}
        loading={loading}
        paginationMode="server"
        paginationModel={paginationModel}
        onPaginationModelChange={onPaginationModelChange}
        rowCount={rowCount}
        rowsPerPageOptions={[10, 25, 50, 100]}
      />

      {/* Dialog for confirming delete */}
      <Dialog
        open={openDialog}
        onClose={handleCancelDelete}
        dir="rtl"
        PaperProps={{
          sx: {
            borderRadius: 3,
            boxShadow: '0 2px 12px rgba(67,56,202,0.07)',
            fontFamily: 'Cairo, Tajawal, sans-serif',
          },
        }}
      >
        <DialogTitle sx={{ fontWeight: 700, color: '#1E1B4B', fontFamily: 'Cairo, Tajawal, sans-serif' }}>تأكيد الحذف</DialogTitle>
        <DialogContent sx={{ color: '#6B7280', fontFamily: 'Cairo, Tajawal, sans-serif' }}>
          هل أنت متأكد أنك تريد حذف هذه الدفعة؟
        </DialogContent>
        <DialogActions sx={{ p: 2, gap: 1 }}>
          <Button
            onClick={handleCancelDelete}
            sx={{
              borderRadius: 2.5,
              textTransform: 'none',
              fontWeight: 700,
              color: '#6B7280',
              fontFamily: 'Cairo, Tajawal, sans-serif',
            }}
          >
            إلغاء
          </Button>
          <Button
            onClick={handleConfirmDelete}
            variant="contained"
            sx={{
              borderRadius: 2.5,
              textTransform: 'none',
              fontWeight: 700,
              bgcolor: '#DC2626',
              fontFamily: 'Cairo, Tajawal, sans-serif',
              boxShadow: 'none',
              '&:hover': { bgcolor: '#B91C1C', boxShadow: 'none' },
            }}
          >
            حذف
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default FinancialTable;
